import React from 'react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryFallbackProps {
  error: Error;
  resetError: () => void;
}

export const ErrorBoundaryFallback = ({ error, resetError }: ErrorBoundaryFallbackProps) => {
  const { t } = useTranslation('common');

  return (
    <div 
      className="flex flex-col items-center justify-center min-h-screen p-4"
      style={{ pointerEvents: 'auto' }}
    >
      <div className="max-w-md w-full text-center bg-background border rounded-lg p-6 shadow-lg">
        <h1 className="text-2xl font-bold text-destructive mb-4">
          {t('error', 'Something went wrong')}
        </h1>
        <p className="text-muted-foreground mb-6">
          The application encountered an error. Please try refreshing the page.
        </p>
        <div className="space-y-4">
          <Button 
            onClick={resetError}
            className="w-full"
            style={{ pointerEvents: 'auto' }}
          >
            {t('retry', 'Try Again')}
          </Button>
          <Button 
            onClick={() => window.location.reload()}
            variant="outline"
            className="w-full"
            style={{ pointerEvents: 'auto' }}
          >
            {t('refresh', 'Refresh Page')}
          </Button>
        </div>
        {process.env.NODE_ENV === 'development' && error && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground">
              Error Details (Dev Mode)
            </summary>
            <pre className="mt-2 text-xs text-destructive bg-destructive/10 p-2 rounded overflow-auto">
              {error.toString()}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};